<template>
  <div id="line-chart" style="height: 150px">
    <canvas id="elevationChart"></canvas>
  </div>
</template>

<script lang="ts">
import { mapGetters } from "vuex";
import { defineComponent } from 'vue';
// 1. Correct Imports for v3/v4
import {
  Chart,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale, // Or TimeScale depending on your X data
  Tooltip,
  Legend,
  ChartConfiguration // Optional: For better typing
} from 'chart.js';
import { MeasurementPrefUtility } from "../../../utils/helpers/MeasurementPref";
import mapboxgl from "mapbox-gl";

// 2. Register Components (Do this once, perhaps before defineComponent or in a setup function if using Composition API)
//    Putting it here works for Options API but isn't ideal if multiple components use Chart.js.
//    A better place might be your main.ts/main.js file.
Chart.register(
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend
);
import { shallowRef } from 'vue';

export default defineComponent({
  data() {
    return {
      elevationMarker: null as mapboxgl.Marker | null, // Add type
      chart: null as Chart | null, // Add type
    };
  },
  watch: {
    chartData: async function (value) {
      this.updateChartData(value);
    },
    measuredGeoJsonChartData: async function (value) {
      if (value) {
        await this.updateChartData(value)
      } else {
        await this.updateChartData(this.chartData)
      }
    },
  },
  computed: mapGetters({
    chartData: 'trailOverviewMap/createChartData',
    measuredGeoJsonChartData: 'trailOverviewMap/measureGeoJsonChartData',
  }),
  mounted() {
    // Consider getting data *after* ensuring the canvas element exists
    this.$nextTick(() => {
      let chartData = this.$store.getters['trailOverviewMap/createChartData'];
      this.updateChartData(chartData);
    });
  },
  beforeDestroy() {
    // Clean up the chart instance
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
    // Clean up the marker
    if (this.elevationMarker) {
      this.elevationMarker.remove();
      this.elevationMarker = null;
    }
  },
  methods: {
    createElevationMarker() {
      // ... (no change needed here)
      const el = document.createElement('div'); // Use div for styling
      el.style.display = 'block';
      el.style.border = '3px solid #fff';
      el.style.cursor = 'pointer';
      el.style.borderRadius = "50%"; // Use 50% for circle
      el.style.width = `14px`; // Adjust size accounting for border
      el.style.height = `14px`;// Adjust size accounting for border
      el.style.backgroundColor = "#bbb";
      el.style.boxSizing = 'border-box'; // Include border in width/height

      // Use new mapboxgl.Marker({element: el})
      const marker = new mapboxgl.Marker({ element: el });
      return marker;
    },
    async updateChartData(chartData) {
      if (chartData && chartData.labels && chartData.datasets) { // Add more robust check
        const canvas = document.getElementById('elevationChart') as HTMLCanvasElement; // Get canvas element
        if (!canvas) {
          console.error("Canvas element 'elevationChart' not found.");
          return;
        }
        const ctx = canvas.getContext('2d'); // Get context
        if (!ctx) {
          console.error("Failed to get 2D context from canvas.");
          return;
        }

        if (this.chart != null) {
          this.chart.data = chartData;
          this.chart.options = this.createOptions(); // Update options if they can change
          this.chart.update();
        } else {
          const config: ChartConfiguration<'line'> = { // Use ChartConfiguration type
            type: 'line',
            data: chartData,
            options: this.createOptions(),
          };
          // Use the imported Chart constructor directly
          this.chart = shallowRef(new Chart(ctx, config));
        }
      } else if (this.chart) {
        // Optional: Clear chart if data becomes null/invalid
        this.chart.data.labels = [];
        this.chart.data.datasets = [];
        this.chart.update('active');
      }
    },
    // 3. Update Options Syntax for v3/v4
    createOptions(): any { // Consider using ChartOptions<'line'> type from chart.js
      const measurementPref = MeasurementPrefUtility.preference();
      const self = this;
      return {
        responsive: true,
        maintainAspectRatio: false,
        elements: { point: { radius: 0 } },
        interaction: { // Use 'interaction' instead of 'hover'
          mode: 'nearest',
          intersect: false,
          axis: 'x', // Define axis for interaction
        },
        plugins: { // Plugins like legend and tooltip go under 'plugins'
          legend: { display: false },
          tooltip: {
            intersect: false,
            mode: 'index', // Often used with axis:'x'
            callbacks: {
              label: function(context) { // Updated callback context
                let label = context.dataset.label || '';
                if (label) {
                  label += ': ';
                }
                if (context.parsed.y !== null) {
                  label += `${context.parsed.y} ${measurementPref.meterSymbol()}`;
                }
                return label;
              }
            }
          },
          // Custom hover logic needs adjustment for v3/v4 event system
          // This requires more research into the specific event structure of v3/v4
          // The old onHover is deprecated. You might use chart events or a custom plugin.
          // See: https://www.chartjs.org/docs/latest/developers/api.html#event-handling

        },
        scales: {
          // Use 'x' and 'y' keys
          x: { // Replaces xAxes array
            grid: { // Use 'grid' instead of 'gridLines'
              display: true,
              color: 'rgba(134,134,134,0.87)',
            },
            ticks: {
              color: 'rgba(255,255,255,0.87)', // Use 'color' instead of 'fontColor'
              // font: { // Font settings are nested
              //   size: 10,
              //   style: 'normal' // e.g., 'normal', 'italic', 'bold'
              // }
              font: { size: 10 }, // Simpler way if only size needed
            }
          },
          y: { // Replaces yAxes array
            grid: {
              display: true,
              color: 'rgba(134,134,134,0.87)',
            },
            ticks: {
              color: 'rgba(255,255,255,0.87)',
              font: { size: 10 },
              callback: function(value: number | string): string | number | null | undefined {
                // The context is different, 'value' is usually enough here
                return `${value} ${measurementPref.meterSymbol()}`;
              },
            },
          }
        },
        animation: {
          duration: 0
        },
        // Custom hover logic needs replacement
        onHover: (event, chartElements, chartInstance) => { // v3/v4 hover signature
          if (event.type === 'mouseout') {
            if (self.elevationMarker) self.elevationMarker.remove();
            return;
          }

          if (chartElements.length) {
            const index = chartElements[0].index;
            // Access lat/lon data carefully - ensure it's attached correctly
            // Assuming your chartData object passed to updateChartData HAS lat/lon arrays
            const lat = this.chart.data.lat?.[index]; // Need to make sure 'lat' exists on data
            const lon = this.chart.data.lon?.[index]; // Need to make sure 'lon' exists on data

            if (lat !== undefined && lon !== undefined && window.map) { // Check window.map exists
              if (self.elevationMarker == null) {
                self.elevationMarker = self.createElevationMarker();
              }
              self.elevationMarker.setLngLat([lon, lat]).addTo(window.map);
            } else if (self.elevationMarker) {
              // Remove marker if data is missing for this point
              self.elevationMarker.remove();
            }
          } else {
            // Remove marker if hovering off active elements
            if (self.elevationMarker) self.elevationMarker.remove();
          }
        },

      }
    }
  }
});
</script>
